'use client';

import { useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import type { Study } from 'content/science/studyTypes';
import ButtonV2 from 'components/shared/ButtonV2';
import CustomLink from 'components/shared/CustomLink';

import type { Motion } from './GradientShader';
import { CARD_SHADER, textureById } from './gradientTextures';

const GradientShader = dynamic(() => import('./GradientShader'), { ssr: false });
import { useHoverShaders } from './useHoverShaders';
import { useReveal } from './useReveal';
import styles from './ResearchLibrary.module.css';

/* §7 — dark card grid (Figma 900:508 / hover 894:8601 / mobile 940:12861).
   On hover a card fades in an animated Dither Gradient image (GradientShader:
   the image's own colours move — waveform, heartbeat, flow, swirl, ripple,
   pulse) and its copy flips to black. White→black page wash lives in DarkBridge. */

type Card = {
  cat: string;
  catLabel: string;
  title: string;
  date: string;
  match: string[];
};

const CARDS: Card[] = [
  {
    cat: 'other',
    catLabel: 'Other',
    title: 'Blood Age and wearable physiology',
    date: 'Aug 2026',
    match: ['blood age', 'biological age'],
  },
  {
    cat: 'sleep',
    catLabel: 'Sleep',
    title: 'The World Cup Sleep Report: what 30 match nights cost 33,513 members’ sleep',
    date: 'Aug 2026',
    match: ['world cup', 'match night'],
  },
  {
    cat: 'fitness',
    catLabel: 'Fitness',
    title: 'Smart-ring VO2max accuracy vs. five equations and lab exercise testing',
    date: 'Jul 2026',
    match: ['vo2', 'vo₂', 'exercise test'],
  },
  {
    cat: 'fitness',
    catLabel: 'Fitness',
    title: 'Wearable-derived fitness age and its lifestyle links in 442 adults',
    date: 'Jul 2026',
    match: ['fitness age', '442'],
  },
  {
    cat: 'heart-health',
    catLabel: 'Heart health',
    title: 'In 211 Ring wearers, five biometrics drift from baseline around a self-reported illness',
    date: 'Jun 2026',
    match: ['illness', 'biometric'],
  },
  {
    cat: 'sleep',
    catLabel: 'Sleep',
    title: 'Pre-bed and nighttime screen use is linked to worse sleep',
    date: 'May 2026',
    match: ['screen', 'pre-bed'],
  },
];

const CATEGORY_COLOR: Record<string, string> = {
  sleep: '#4f5cf2',
  'heart-health': '#db2626',
  'womens-health': '#db2677',
  'metabolic-health': '#e0952b',
  sports: '#2fb17a',
  environment: '#2bb0c9',
  other: '#9aa0aa',
};

const DEFAULT_SHADER: { images: string[]; motions: Motion[] } = {
  images: ['21', '23', '13', '26', '06', '11'],
  motions: ['waveform', 'waveform', 'heartbeat', 'flow', 'ripple', 'waveform'],
};

const Arrow = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M4 12L12 4M12 4H5.5M12 4V10.5"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function ResearchLibrary({ studies }: { studies: Study[] }) {
  const t = useTranslations('science');
  // header lines, then the six cards as they enter, then "View all"
  const revealRef = useReveal<HTMLDivElement>();

  // desktop + a real hover pointer only; phones never mount the shaders
  const hoverShaders = useHoverShaders();
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [armed, setArmed] = useState<readonly boolean[]>(() => CARDS.map(() => false));

  const arm = (i: number) =>
    setArmed((prev) => (prev[i] ? prev : prev.map((v, j) => (j === i ? true : v))));

  /* each card resolves to its study record; the tag + link come from that record
     (the CMS category the studies page filters on), not from the card list */
  const studyFor = (card: Card): Study | undefined =>
    studies.find((s) => card.match.some((k) => s.title.toLowerCase().includes(k)));
  const hrefFor = (m: Study | undefined): string =>
    m ? (m.externalUrl ?? `/science/studies/${m.slug}/`) : '/science/studies/';

  return (
    <section
      id="science-library"
      className={styles.section}
      aria-labelledby="science-library-title"
    >
      <div ref={revealRef} className={styles.inner}>
        <header className={styles.head}>
          <p className={styles.eyebrow} data-reveal>
            {t('science.landing.library.eyebrow')}
          </p>
          <h2 id="science-library-title" className={styles.heading} data-reveal>
            {t('science.landing.library.title')}
          </h2>
          <p className={styles.sub} data-reveal>
            {t('science.landing.library.lede')}
          </p>
        </header>

        <div className={styles.grid} role="list">
          {CARDS.map((c, i) => {
            const study = studyFor(c);
            const cat = study?.category ?? c.cat;
            const catLabel = study?.categoryLabel ?? c.catLabel;
            const color = CATEGORY_COLOR[cat] ?? CATEGORY_COLOR.other;
            const href = hrefFor(study);
            const tex = textureById(DEFAULT_SHADER.images[i] ?? '13');
            /* dark textures keep the white copy; light ones flip it to black */
            const tone = tex.lum < 0.5 ? 'dark' : 'light';
            const external = href.startsWith('http');
            const on = hoverIdx === i;

            const enter = () => {
              if (!hoverShaders) return;
              setHoverIdx(i);
              arm(i);
            };
            const leave = () => setHoverIdx((v) => (v === i ? null : v));

            const inner = (
              <>
                {armed[i] ? (
                  <span className={styles.shader} aria-hidden="true">
                    <GradientShader
                      className={styles.gradient}
                      src={tex.src}
                      seed={i * 2.3 + 0.7}
                      params={{
                        motion: DEFAULT_SHADER.motions[i] ?? 'flow',
                        ...CARD_SHADER,
                      }}
                    />
                  </span>
                ) : null}
                <span className={styles.meta}>
                  <span className={styles.tag}>
                    <span className={styles.tagDot} style={{ background: color }} />
                    <span className={styles.tagLabel}>{catLabel}</span>
                  </span>
                  <span className={styles.date}>{c.date}</span>
                </span>
                <span className={styles.title}>{c.title}</span>
                <span className={styles.arrow}>
                  <Arrow />
                </span>
              </>
            );

            const common = {
              role: 'listitem' as const,
              className: styles.card,
              'data-on': on ? 'true' : undefined,
              'data-tone': tone,
              'data-reveal': '',
              onMouseEnter: enter,
              onMouseLeave: leave,
            };

            return external ? (
              <a
                key={c.title}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                {...common}
              >
                {inner}
              </a>
            ) : (
              <CustomLink key={c.title} href={href} {...common}>
                {inner}
              </CustomLink>
            );
          })}
        </div>

        <div className={styles.viewAll} data-reveal>
          <ButtonV2
            href={{ pathname: "/science/studies/", hash: "research-studies" }}
            variant="glassLight"
            label={t('science.landing.library.viewAll')}
          />
        </div>
      </div>
    </section>
  );
}
